import type { Ledger, StoryBible } from '../types';
import { formatCanonEntriesForPrompt } from './canonInputNormalization.service';

export type LedgerFieldPresence =
  | 'omitted'
  | 'explicit_unchanged'
  | 'explicit_update'
  | 'explicit_empty'
  | 'malformed';

export function isValidExplicitEmptyMarker(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return (
    record.__explicitEmpty === true &&
    typeof record.resolutionEvidence === 'string' &&
    record.resolutionEvidence.trim().length > 0
  );
}

export function classifyRawLedgerField(
  rawData: Record<string, unknown>,
  key: string,
): LedgerFieldPresence {
  if (!(key in rawData)) return 'omitted';
  const value = rawData[key];
  if (value === null || value === undefined) return 'omitted';
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    if ((value as Record<string, unknown>).__explicitEmpty === true) {
      return isValidExplicitEmptyMarker(value) ? 'explicit_empty' : 'malformed';
    }
  }
  if (Array.isArray(value)) return 'explicit_update';
  if (typeof value === 'object') return 'explicit_update';
  if (typeof value === 'string') return 'explicit_update';
  return 'malformed';
}

function wrapArray(arr: unknown[] | undefined, bible?: StoryBible) {
  if (!Array.isArray(arr)) return [];
  return arr.map((item) => {
    if (typeof item === 'string') {
      return {
        content: item,
        canonId: bible?.canonId,
        projectId: bible?.canonId,
        createdAt: new Date().toISOString(),
        canonStatus: 'active',
      };
    }
    return item;
  });
}

function wrapObj(obj: Record<string, unknown> | undefined, existing: Record<string, unknown>, bible?: StoryBible) {
  if (!obj || typeof obj !== 'object') return { ...existing };
  const result: Record<string, unknown> = { ...existing };
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (!value || typeof value !== 'object') continue;
    result[key] = {
      ...(value as Record<string, unknown>),
      canonId: bible?.canonId,
      projectId: bible?.canonId,
      createdAt: new Date().toISOString(),
      canonStatus: 'active',
    };
  }
  return result;
}

function preserveOrWrapArray(
  rawData: Record<string, unknown>,
  key: keyof Ledger,
  prior: Ledger,
  bible?: StoryBible,
): Ledger[keyof Ledger] {
  const presence = classifyRawLedgerField(rawData, key as string);
  const priorValue = prior[key];
  if (presence === 'omitted' || presence === 'malformed') {
    return (priorValue as Ledger[keyof Ledger]) || ([] as Ledger[keyof Ledger]);
  }
  const incoming = rawData[key as string];
  if (!Array.isArray(incoming)) {
    if (presence === 'explicit_empty') {
      return [] as Ledger[keyof Ledger];
    }
    return (priorValue as Ledger[keyof Ledger]) || ([] as Ledger[keyof Ledger]);
  }
  if (incoming.length === 0 && presence !== 'explicit_empty') {
    return (priorValue as Ledger[keyof Ledger]) || ([] as Ledger[keyof Ledger]);
  }
  return wrapArray(incoming, bible) as Ledger[keyof Ledger];
}

function preserveScalar(
  rawData: Record<string, unknown>,
  key: 'momentum' | 'arc_stage' | 'current_time',
  prior: Ledger,
): string {
  const presence = classifyRawLedgerField(rawData, key);
  if (presence === 'omitted' || presence === 'malformed') {
    return String(prior[key] || '');
  }
  const incoming = rawData[key];
  return typeof incoming === 'string' ? incoming : String(prior[key] || '');
}

export function mergeAuthoritativeLedger(params: {
  priorLedger: Ledger;
  rawData: Record<string, unknown>;
  bible?: StoryBible;
  physicalMerged: Pick<Ledger, 'continuity_objects' | 'physical_continuity_obligations'>;
  latestStateDelta?: Ledger['latestStateDelta'];
}): Ledger {
  const { priorLedger, rawData, bible, physicalMerged, latestStateDelta } = params;

  const merged: Ledger = {
    ...priorLedger,
    characters: wrapObj(
      rawData.characters as Record<string, unknown> | undefined,
      priorLedger.characters || {},
      bible,
    ) as Ledger['characters'],
    locations: wrapObj(
      rawData.locations as Record<string, unknown> | undefined,
      priorLedger.locations || {},
      bible,
    ) as Ledger['locations'],
    relationships: wrapObj(
      rawData.relationships as Record<string, unknown> | undefined,
      priorLedger.relationships || {},
      bible,
    ) as Ledger['relationships'],
    factions: wrapObj(
      rawData.factions as Record<string, unknown> | undefined,
      priorLedger.factions || {},
      bible,
    ) as Ledger['factions'],
    inventory: preserveOrWrapArray(rawData, 'inventory', priorLedger, bible) as Ledger['inventory'],
    plot_points: preserveOrWrapArray(rawData, 'plot_points', priorLedger, bible) as Ledger['plot_points'],
    resolved_plot_points: preserveOrWrapArray(
      rawData,
      'resolved_plot_points',
      priorLedger,
      bible,
    ) as Ledger['resolved_plot_points'],
    historical_events: preserveOrWrapArray(
      rawData,
      'historical_events',
      priorLedger,
      bible,
    ) as Ledger['historical_events'],
    world_rules: preserveOrWrapArray(rawData, 'world_rules', priorLedger, bible) as Ledger['world_rules'],
    state_records: preserveOrWrapArray(rawData, 'state_records', priorLedger, bible) as Ledger['state_records'],
    momentum: preserveScalar(rawData, 'momentum', priorLedger),
    arc_stage: preserveScalar(rawData, 'arc_stage', priorLedger),
    current_time: preserveScalar(rawData, 'current_time', priorLedger),
    continuity_objects: physicalMerged.continuity_objects,
    physical_continuity_obligations: physicalMerged.physical_continuity_obligations,
    latestStateDelta: latestStateDelta || priorLedger.latestStateDelta,
    ledgerSchemaVersion: 2,
    last_updated: new Date().toISOString(),
  };

  return merged;
}

export function summarizeLedgerPreservation(
  priorLedger: Ledger,
  mergedLedger: Ledger,
): {
  charactersRetained: number;
  locationsRetained: number;
  inventoryRetained: number;
  plotThreadsRetained: number;
} {
  return {
    charactersRetained: Object.keys(mergedLedger.characters || {}).length,
    locationsRetained: Object.keys(mergedLedger.locations || {}).length,
    inventoryRetained: (mergedLedger.inventory || []).length,
    plotThreadsRetained: (mergedLedger.plot_points || []).length,
  };
}

export function formatLedgerRecordForPrompt(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record.content === 'string') return record.content;
    if (typeof record.name === 'string' && typeof record.desc === 'string') {
      return `${record.name}: ${record.desc}`;
    }
    if (typeof record.name === 'string') return record.name;
    try {
      return JSON.stringify(record);
    } catch {
      return '[unserializable record]';
    }
  }
  return String(value);
}

export function formatLedgerArrayForPrompt(
  entries: Array<string | Record<string, unknown>> | undefined,
  emptyLabel = 'None',
): string {
  if (!entries?.length) return emptyLabel;
  return entries.map((entry) => formatLedgerRecordForPrompt(entry)).filter(Boolean).join('; ');
}

export function formatResolvedFactsForPrompt(ledger: Ledger): string {
  return formatCanonEntriesForPrompt(ledger.resolved_plot_points)
    .split(', ')
    .filter((entry) => entry && entry !== 'None')
    .map((entry) => `[KNOWN: ${entry}]`)
    .join(' ');
}
